import { useRef, useCallback, useEffect } from "react";
import { logEvent } from "firebase/analytics";
import { analytics } from "./firebase";

/**
 * Debounced Firebase Analytics logger
 * @param {string} eventName - The event name (snake_case recommended, e.g. "button_clicked")
 * @param {number} delay - Debounce delay in ms (default 300-500ms)
 */
export const useDebouncedLog = (eventName, delay = 400) => {
  const timeoutRef = useRef(null);

  const debouncedLog = useCallback(
    (params = {}) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        try {
          logEvent(analytics, eventName, {
            ...params,
            // Remove or make conditional — having button_name = eventName is redundant
            // page: "Login",           // Better to pass dynamically if page changes
            // timestamp: Date.now(),   // Not needed — Firebase adds its own timestamp
          });

          if (import.meta.env.DEV) {
            console.log(`[Analytics] ${eventName}`, params);
          }
        } catch (error) {
          console.error(`Analytics logging failed for event "${eventName}":`, error);
        }
      }, delay);
    },
    [eventName, delay]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedLog;
};